import React, { useMemo } from 'react'
import { useIntl } from 'react-intl'

import styled, { useTheme } from 'styled-components/native'

import {
  BestAndWorstInsightStrings,
  InsightType,
  ProviderInsightsCompetencyCategory,
  ProviderInsightsSubAreaGerundFormatLabels,
  ProviderInsightsSubAreaName,
} from '@lyrahealth-inc/shared-app-logic/src/features/careInsights/constants'
import { isSkillInGlossary } from '@lyrahealth-inc/shared-app-logic/src/features/careInsights/skillsGlossary'
import {
  ProviderInsightsBestAndWorst,
  ProviderInsightsClientAndSessionDate,
} from '@lyrahealth-inc/shared-app-logic/src/models/careInsights/CareInsights'

import { BulletedList, BulletItem } from '../../atoms/bulletedList/BulletedList'
import { BulletIcon } from '../../atoms/icons/BulletIcon'
import { Link } from '../../atoms/link/Link'
import { LoadingSkeleton } from '../../atoms/loadingSkeleton/LoadingSkeleton'
import { BodyTextSize } from '../../styles/typeStyles'
import { tID } from '../../utils/utils'

export interface BestAndWorstInsightsProps {
  bestAndWorst?: ProviderInsightsBestAndWorst
  competencyAreaName?: ProviderInsightsCompetencyCategory
  isLoading?: boolean
  onClientPress: (clientId: string) => void
  /** When provided, skills that have a glossary entry are underlined and call this on press. */
  onSkillPress?: (subAreaName: ProviderInsightsSubAreaName) => void
  getClientInfoFromIds: (clientIds: { value: string | number }[]) => { name: string; clientId: string }[]
  testIDPrefix?: string
}

const LoadingBestAndWorstInsightsContainer = styled.View(({ theme }) => ({
  marginLeft: theme.spacing['12px'],
  maxWidth: '100%',
  marginTop: theme.spacing['4px'],
}))

const BulletIconContainer = styled.View(({ theme }) => ({
  paddingTop: theme.spacing['8px'],
}))

// A template literal avoids a type cast: textDecorationThickness is a web-only property missing from React Native's TextStyle
const SkillLink = styled(Link)`
  text-decoration-style: dotted;
  text-decoration-color: ${({ theme }) => theme.colors.textPrimary};
  text-decoration-thickness: 2px;
`

export const BestAndWorstInsights: React.FC<BestAndWorstInsightsProps> = ({
  bestAndWorst,
  competencyAreaName,
  isLoading,
  onClientPress,
  onSkillPress,
  getClientInfoFromIds,
  testIDPrefix,
}) => {
  const { colors, spacing } = useTheme()
  const { formatList } = useIntl()

  const {
    bestScoringSubArea,
    bestScoringClientIdsAndSessionDates,
    worstScoringSubArea,
    worstScoringClientIdsAndSessionDates,
  } = bestAndWorst ?? {}

  const bestAndWorstInsightsBullets = useMemo(() => {
    const renderClientLinks = (
      clientsWithSessionDates: ProviderInsightsClientAndSessionDate[] | undefined,
      isFollowedByPeriod: boolean,
    ) => {
      const MAX_CLIENT_LINKS_TO_DISPLAY = 3
      // Filter out sessions with invalid client IDs first, then slice to get the desired count
      const allSessions = clientsWithSessionDates ?? []
      const validSessions = allSessions.filter((sessionInfo) => {
        const [clientInfo] = getClientInfoFromIds([sessionInfo.clientId])
        return clientInfo !== undefined
      })
      const sessionsToDisplay = validSessions.slice(0, MAX_CLIENT_LINKS_TO_DISPLAY)

      return formatList(
        sessionsToDisplay.map((sessionInfo, index) => {
          const [clientInfo] = getClientInfoFromIds([sessionInfo.clientId])

          const date = new Date(sessionInfo.sessionDate)
          const month = date.getMonth() + 1
          const day = date.getDate()
          const period = isFollowedByPeriod && index === sessionsToDisplay.length - 1 ? '' : '.'
          const displayText = `${clientInfo.name}${period} (${month}/${day})`

          return (
            <Link
              key={`${clientInfo.clientId}-${sessionInfo.sessionDate}`}
              text={displayText}
              testID={tID(`${testIDPrefix ?? 'BestAndWorstInsights'}-clientLink-${clientInfo.clientId}`)}
              onPress={() => onClientPress(clientInfo.clientId)}
              size={BodyTextSize.SMALL}
              underline
            />
          )
        }),
        { type: 'conjunction' },
      )
    }

    const renderSkill = (subAreaName: ProviderInsightsSubAreaName): React.ReactNode => {
      const skillLabel = ProviderInsightsSubAreaGerundFormatLabels[subAreaName]
      if (!onSkillPress || !isSkillInGlossary(subAreaName)) {
        return skillLabel
      }
      return (
        <SkillLink
          text={skillLabel}
          testID={tID(`${testIDPrefix ?? 'BestAndWorstInsights'}-skillLink-${subAreaName}`)}
          onPress={() => onSkillPress(subAreaName)}
          size={BodyTextSize.SMALL}
          color={colors.textSecondary}
          underline
        />
      )
    }

    const getInsightString = ({
      skill,
      clientsWithSessionDates,
      type,
    }: {
      skill: React.ReactNode
      clientsWithSessionDates?: ProviderInsightsClientAndSessionDate[]
      type: InsightType
    }): React.ReactNode => {
      const getHashIndex = (seed: string, max: number) => {
        let hash = 0
        for (let i = 0; i < seed.length; i++) {
          hash = (hash << 5) - hash + seed.charCodeAt(i)
          hash |= 0
        }
        return Math.abs(hash) % max
      }

      if (!competencyAreaName) {
        return null
      }

      const insightStrings = BestAndWorstInsightStrings[competencyAreaName]?.[type]
      if (!insightStrings || insightStrings.length === 0) {
        return null
      }

      const randomIndex = getHashIndex(`${bestScoringSubArea}-${competencyAreaName}`, insightStrings.length)
      const template = insightStrings[randomIndex]

      // Check if [Name] or [Name's] is immediately followed by a period in the template
      const isNameFollowedByPeriod = /\[Name\]\./.test(template) || /\[Name's\]\./.test(template)
      const clientLinks = renderClientLinks(clientsWithSessionDates, isNameFollowedByPeriod)

      const parts = template.split(/(\[SKILL\]|\[Name\]|\[Name's\])/g)
      return parts.map((part, index) => {
        if (part === '[SKILL]') {
          return <React.Fragment key={index}>{skill}</React.Fragment>
        }
        if (part === '[Name]' || part === "[Name's]") {
          return <React.Fragment key={index}>{clientLinks}</React.Fragment>
        }
        return part
      })
    }

    const points: BulletItem[] = []
    if (
      bestScoringSubArea &&
      bestScoringSubArea in ProviderInsightsSubAreaGerundFormatLabels &&
      bestScoringClientIdsAndSessionDates &&
      bestScoringClientIdsAndSessionDates.length > 0
    ) {
      const skill = renderSkill(bestScoringSubArea)
      const bestScoringInsightValue = getInsightString({
        skill,
        clientsWithSessionDates: bestScoringClientIdsAndSessionDates,
        type: InsightType.STRENGTH,
      })

      if (bestScoringInsightValue) {
        points.push({
          value: bestScoringInsightValue,
          testID: `${testIDPrefix ?? 'BestAndWorstInsights'}-bestScoringSubArea`,
          bulletIcon: (
            <BulletIconContainer>
              <BulletIcon size={2} />
            </BulletIconContainer>
          ),
          textColor: colors.textSecondary,
        })
      }
    }
    if (
      worstScoringSubArea &&
      worstScoringSubArea in ProviderInsightsSubAreaGerundFormatLabels &&
      worstScoringClientIdsAndSessionDates &&
      worstScoringClientIdsAndSessionDates.length > 0
    ) {
      const skill = renderSkill(worstScoringSubArea)
      const worstScoringInsightValue = getInsightString({
        skill,
        clientsWithSessionDates: worstScoringClientIdsAndSessionDates,
        type: InsightType.OPPORTUNITY,
      })

      if (worstScoringInsightValue) {
        points.push({
          value: worstScoringInsightValue,
          testID: `${testIDPrefix ?? 'BestAndWorstInsights'}-worstScoringSubArea`,
          bulletIcon: (
            <BulletIconContainer>
              <BulletIcon size={2} />
            </BulletIconContainer>
          ),
          textColor: colors.textSecondary,
        })
      }
    }
    return points
  }, [
    bestScoringSubArea,
    bestScoringClientIdsAndSessionDates,
    competencyAreaName,
    formatList,
    onClientPress,
    onSkillPress,
    testIDPrefix,
    worstScoringSubArea,
    worstScoringClientIdsAndSessionDates,
    getClientInfoFromIds,
    colors.textSecondary,
  ])

  if (isLoading) {
    return (
      <LoadingBestAndWorstInsightsContainer testID={tID(`${testIDPrefix ?? 'BestAndWorstInsights'}-loadingContainer`)}>
        <LoadingSkeleton
          width={500}
          maxWidth='100%'
          height={16}
          marginTop={4}
          testID={tID(`${testIDPrefix ?? 'BestAndWorstInsights'}-bestInsightLoadingSkeleton`)}
        />
        <LoadingSkeleton
          width={450}
          maxWidth='100%'
          height={16}
          marginTop={8}
          testID={tID(`${testIDPrefix ?? 'BestAndWorstInsights'}-worstInsightLoadingSkeleton`)}
        />
      </LoadingBestAndWorstInsightsContainer>
    )
  }
  return bestAndWorstInsightsBullets.length > 0 ? (
    <BulletedList
      data={bestAndWorstInsightsBullets}
      size={BodyTextSize.SMALL}
      bulletColor={colors.textSecondary}
      spaceBetween={0}
      style={{ marginLeft: spacing['12px'] }}
    />
  ) : null
}
